import { debounce, generateId, memoizeFunction } from '@agilepoint/utils-common';
import * as React from 'react';
import styled from 'styled-components';
import { IFileUploadProps, IFileInfo } from './file-upload.types';
import { getAllFilesInfo, useFileRepository } from './utils';
import { FilePreview } from './parts/file-preview';
import { Separator } from '../separator';
import { ActionButton, FlexBox, selectThemeSemantics, StackPanel, Typography } from '@agilepoint/wat-react-ux';
import { useTranslate } from '@agilepoint/wat-react';

const IMAGE_EXTENSION_REGEX = /^image\//i;

const DefaultFileInput = styled.input.attrs({
    type: 'file'
})`
    display: none;
`;

const parseFileInfo = (settledResult: PromiseSettledResult<IFileInfo>): IFileInfo => (settledResult.status === 'fulfilled' ? settledResult.value : settledResult.reason);

const FlexBoxWithBorder = styled(FlexBox).attrs({
    className: String('ap-fileUpload-field')
}) <{ $selectedFiles: boolean, $isFileInDropZone: boolean }>`
    width: 100%;
    border-style: dashed;
    flex-shrink: 0;
    height: 40px;
    cursor: ${({ $selectedFiles }) => ($selectedFiles ? 'default' : 'pointer')};
    background-color: ${({ theme: { mobileAppAccelerator, semantics }, $selectedFiles }) => ($selectedFiles ? semantics.palette.white : mobileAppAccelerator.semanticColors.grayVariant6)};
    border-width: ${({ theme: { fileUpload: { composites } }, $isFileInDropZone }) => ($isFileInDropZone ? '2px' : composites.borderWidth)};
    border-color: ${({ theme: { mobileAppAccelerator, semantics }, $isFileInDropZone }) => ($isFileInDropZone ? semantics.palette.themePrimary : mobileAppAccelerator.semanticColors.grayVariant4)};
    border-radius: ${({ theme: { fileUpload: { composites } } }) => composites.borderRadius};
`;

const Wrapper = styled(StackPanel).attrs({
    className: String('ap-fileUpload-wrapper')
})`
    height: 100%;
    width: 100%;
    min-width: 250px;
    box-sizing: border-box;
    background-color: ${({ theme: { fileUpload: { colors } } }) => colors.backgroundColor};
`;

const stopEvent = (ev: React.SyntheticEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
};

const StyledHorizontalSeparator = styled(Separator)`
    position: static;
    width: 100%;
    background-color: ${selectThemeSemantics(({ palette }) => palette.neutralTertiary)};
`;

const onRenderSeperator = () => (
    <StyledHorizontalSeparator orientation="horizontal" />
);

const trim = (extension: string) => extension.replace('.', '').trim();
const generateAcceptedTypeRegex = memoizeFunction((extensions: string) => new RegExp(`.(${extensions.split(',').map(trim).join('|')})$`, 'i'));

const filterValidFiles = (files: FileList, accept = '*'): File[] => Array.from(files).filter(file => {
    if (accept === '*') {
        return true;
    }
    if (IMAGE_EXTENSION_REGEX.test(accept) && IMAGE_EXTENSION_REGEX.test(file.type)) {
        return true;
    }
    const acceptedTypesRegex = generateAcceptedTypeRegex(accept);
    return acceptedTypesRegex.test(file.name);
});

export const FileUpload = React.memo((props: IFileUploadProps) => {
    const translate = useTranslate();
    const labelTranslated: string = translate('common.actions.selectFile');
    const {
        elementRef, className, hidden, label = labelTranslated, allowDuplicateFiles,
        accept, multiple, initialSelectedFiles, readMode = 'readAsText', onRenderItemPreview, onChange } = props;
    const id = React.useMemo(() => generateId(), []);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const { selectedFiles, remove, upsert } = useFileRepository({ initialFiles: initialSelectedFiles, multiple, onChange, allowDuplicateFiles });
    const [isFileInDropZone, setFileDrop] = React.useState(false);

    const _onButtonClick = React.useCallback(() => {
        const { current } = inputRef;
        current?.click();
    }, []);

    const renderPreviewItem = React.useCallback((info: IFileInfo) => (
        <FilePreview {...info} key={info.name} onRemove={remove}
            onRenderItemPreview={onRenderItemPreview} />
    ), [onRenderItemPreview, remove]);

    const loadSelectedFiles = React.useCallback(async (fileList: File[] | null) => {
        if (!fileList || fileList.length === 0) {
            return;
        }
        const result = (await getAllFilesInfo(fileList, readMode)).map(parseFileInfo);
        upsert(result);
        const { current } = inputRef;
        if (!current) {
            return;
        }
        current.value = '';
    }, [readMode, upsert]);

    const _onFileSelect = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
        loadSelectedFiles(ev.target.files ? Array.from(ev.target.files) : null);
    }, [loadSelectedFiles]);

    const _onDropFiles = React.useCallback((ev: React.DragEvent) => {
        const fileList = filterValidFiles(ev.dataTransfer.files, accept);
        stopEvent(ev);
        setFileDrop(false);
        if (fileList.length === 0) {
            return;
        }
        loadSelectedFiles(fileList);
    }, [loadSelectedFiles, accept]);

    const _onDragEnter = debounce((ev: React.SyntheticEvent) => {
        setFileDrop(true);
        stopEvent(ev);
    }, 200);

    const _onDragLeave = debounce((ev: React.SyntheticEvent) => {
        setFileDrop(false);
        stopEvent(ev);
    }, 200);

    const renderFileDropUI = React.useCallback(() => {

        const checkForDropZone = () => (
            <>
                {
                    isFileInDropZone
                        ? <Typography.Small>Drop your files here</Typography.Small>
                        : <ActionButton type="button" label={label} iconName="ap-export2.outline" size="small" />
                }
            </>
        );

        return (
            <StackPanel gap="25" alignment="center">
                <label htmlFor={id}>
                    <StackPanel orientation="vertical" gap="10">
                        <DefaultFileInput id={id} type="file" ref={inputRef} accept={accept} multiple={multiple} onChange={_onFileSelect} />
                        {checkForDropZone()}
                    </StackPanel>
                </label>
            </StackPanel>
        );
    }, [_onFileSelect, accept, id, isFileInDropZone, label, multiple]);

    return hidden ? null : (
        <Wrapper gap="15" className={className} ref={elementRef} orientation="vertical" alignment="center">
            <FlexBoxWithBorder justifyContent="center" $isFileInDropZone={isFileInDropZone} $selectedFiles={!!selectedFiles.length} alignItems="center" onDrop={_onDropFiles}
                onDragEnter={_onDragEnter} onDragOver={stopEvent} onDragLeave={_onDragLeave} onClick={_onButtonClick}>
                {
                    selectedFiles.length ?
                        (
                            <StackPanel className="ap-fileUpload-previewContainer" width="100%" orientation="vertical" gap="16" separator={onRenderSeperator}>
                                {selectedFiles.map(renderPreviewItem)}
                            </StackPanel>
                        )
                        : renderFileDropUI()
                }
            </FlexBoxWithBorder>
        </Wrapper>
    );
});

FileUpload.displayName = 'FileUpload';
